import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ImageUpload from '../components/ImageUpload';
import Modal from '../components/Modal';
import { categoriesApi } from '../lib/api';

import type { Html5Qrcode as Html5QrcodeType } from 'html5-qrcode';
import { hasPhysicalAddress } from '../utils/locations';

interface TempImage {
  id: string;
  url: string;
  originalFilename: string;
  format: string;
}

export default function ListItem() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const conditions = [
    { value: 'LIKE_NEW', label: 'Like New' },
    { value: 'GOOD', label: 'Good' },
    { value: 'FAIR', label: 'Fair' },
  ];

  // Previously a hardcoded array duplicated across this file, EditItem.tsx, and
  // FilterSidebar.tsx (with a comment admitting the sync-by-hand risk) -- now a real
  // admin-managed table (see AdminDashboard.tsx's Categories tab), fetched once here.
  const [categories, setCategories] = useState<string[]>([]);
  useEffect(() => {
    categoriesApi.getAll()
      .then((res) => setCategories(res.data.data.map((c: { name: string }) => c.name)))
      .catch(() => setCategories([]));
  }, []);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Power Tools',
    condition: 'GOOD',
    sku: '',
    serialNumber: '',
    price_daily: '',
    price_weekly: '',
    price_monthly: '',
    minRentalDays: '',
    pickupLocationLabel: '',
    weightLbs: '',
    lengthIn: '',
    widthIn: '',
    heightIn: '',
    securityDeposit: '',
  });
  const [requiresDeposit, setRequiresDeposit] = useState(false);

  const pickupLocations = user?.metadata?.locations?.pickup || [];
  const defaultPickup = pickupLocations.find(loc => loc.isDefault) || pickupLocations[0];

  useEffect(() => {
    if (defaultPickup && !formData.pickupLocationLabel) {
      setFormData(prev => ({ ...prev, pickupLocationLabel: defaultPickup.name }));
    }
  }, [defaultPickup]);
  const [previewMeta, setPreviewMeta] = useState<Array<{ url: string; file?: File; tempId?: string; uploadFailed?: boolean }>>([]);
  const [uploadedTempImages, setUploadedTempImages] = useState<TempImage[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState(0);
  const [error, setError] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasListed, setHasListed] = useState(false);
  const [showSerialWarning, setShowSerialWarning] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const scannerRef = useRef<Html5QrcodeType | null>(null);

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setIsSnackbarVisible(true);
    window.setTimeout(() => {
      setIsSnackbarVisible(false);
    }, 5000);
  };

  useEffect(() => {
    if (error) {
      showSnackbar(error);
    }
  }, [error]);

  const normalizePriceInput = (value: string): string => {
    // Allow only digits and at most one decimal point.
    let cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

  const formatPriceBlur = (value: string): string => {
    if (!value) return '';
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return value;
    return num.toFixed(2);
  };

  const PRICE_FIELDS = ['price_daily', 'price_weekly', 'price_monthly', 'securityDeposit'];

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (type === 'text' && PRICE_FIELDS.includes(name)) {
      setFormData({ ...formData, [name]: normalizePriceInput(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (PRICE_FIELDS.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: formatPriceBlur(value) }));
    }
  };

  const handleImagesSelected = async (files: File[]) => {
    const totalImages = uploadedTempImages.length + previewMeta.length + files.length;
    if (totalImages > 8) {
      setError('Maximum 8 images allowed');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in first');
      return;
    }

    const entries = files.map(file => ({ url: URL.createObjectURL(file), file }));
    setPreviewMeta(prev => [...prev, ...entries]);

    setUploadProgress(prev => {
      const updated = [...prev];
      for (let i = 0; i < files.length; i++) {
        updated[uploadedTempImages.length + prev.length + i] = 0;
      }
      return updated;
    });
    setIsUploading(true);

    for (let i = 0; i < entries.length; i++) {
      const { file } = entries[i];
      const listIndex = uploadedTempImages.length + (previewMeta.length - entries.length) + i;

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('images', file);
        const response = await fetch('/api/v1/images/temp', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload,
        });

        if (!response.ok) {
          if (response.status === 413) {
            throw new Error('Image is too large. Please choose an image smaller than 10 MB, or compress it before uploading.');
          }
          const contentType = response.headers.get('content-type') || '';
          const data = contentType.includes('application/json') ? await response.json() : {};
          throw new Error(data.error || `Failed to upload image (HTTP ${response.status})`);
        }

        const data = await response.json();
        const image = data.images[0];
        setUploadedTempImages(prev => [...prev, {
          id: image.id,
          url: image.tempUrl,
          originalFilename: image.originalFilename,
          format: image.format,
        }]);
        setPreviewMeta(prev => {
          const updated = [...prev];
          updated[listIndex] = { ...updated[listIndex], tempId: image.id };
          return updated;
        });
        setUploadProgress(prev => {
          const updated = [...prev];
          updated[listIndex] = 100;
          return updated;
        });
      } catch (err: any) {
        setError(err.message || 'Failed to upload image');
        setUploadProgress(prev => {
          const updated = [...prev];
          updated[listIndex] = 0;
          return updated;
        });
        setPreviewMeta(prev => {
          const updated = [...prev];
          updated[listIndex] = { ...updated[listIndex], uploadFailed: true };
          return updated;
        });
      }
    }

    setIsUploading(false);
  };

  const handleImageRemove = (index: number) => {
    const token = localStorage.getItem('token');
    const meta = previewMeta[index];

    if (index < uploadedTempImages.length) {
      const tempImage = uploadedTempImages[index];
      if (token) {
        fetch(`/api/v1/images/temp/${tempImage.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(err => console.error('Failed to delete temp image:', err));
      }
      setUploadedTempImages(prev => prev.filter((_, i) => i !== index));
    } else if (token && meta?.tempId && uploadedTempImages.find(img => img.id === meta.tempId)) {
      // Image finished uploading but is after the temp images slice because another was removed earlier
      fetch(`/api/v1/images/temp/${meta.tempId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(err => console.error('Failed to delete temp image:', err));
      setUploadedTempImages(prev => prev.filter(img => img.id !== meta.tempId));
    }

    URL.revokeObjectURL(meta.url);
    setPreviewMeta(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => prev.filter((_, i) => i !== index));

    if (index === defaultImageIndex && previewMeta.length > 1) {
      setDefaultImageIndex(0);
    } else if (index < defaultImageIndex) {
      setDefaultImageIndex(prev => prev - 1);
    }
  };

  const handleSetDefault = (index: number) => {
    setDefaultImageIndex(index);
  };

  const cleanupTempImages = async (imageIds: string[]) => {
    const token = localStorage.getItem('token');
    if (!token || imageIds.length === 0) return;

    await Promise.all(
      imageIds.map(id =>
        fetch(`/api/v1/images/temp/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(err => console.error(`Failed to cleanup temp image ${id}:`, err))
      )
    );
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch (err) {
        // Scanner may already be stopped or not started
      }
      scannerRef.current = null;
    }
    setShowScanner(false);
    setScannerError('');
  };

  const startScanner = async () => {
    setShowScanner(true);
    setScannerError('');
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('serial-scanner-region');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          setFormData(prev => ({ ...prev, serialNumber: decodedText }));
          void stopScanner();
        },
        () => {
          // Continuous scanning; errors are ignored until success
        }
      );
    } catch (err) {
      setScannerError('Could not start camera. Please allow camera access and try again.');
      scannerRef.current = null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.description.trim().length > 0 && formData.description.trim().length < 20) {
      setError('Description must be at least 20 characters.');
      document.getElementById('description')?.focus();
      return;
    }

    if (!formData.serialNumber.trim()) {
      setShowSerialWarning(true);
      return;
    }

    await doSubmit();
  };

  const doSubmit = async () => {
    setIsLoading(true);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in first');
      setIsLoading(false);
      return;
    }

    if (uploadedTempImages.length === 0) {
      setError('At least one photo is required. Make sure image uploads finish successfully before listing.');
      setIsLoading(false);
      return;
    }

    if (previewMeta.some(m => m.uploadFailed)) {
      setError('One or more photos failed to upload. Please remove failed images and re-upload them.');
      setIsLoading(false);
      return;
    }

    try {
      // Create item with temp image references
      const response = await fetch('/api/v1/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          pickupLocationLabel: formData.pickupLocationLabel || undefined,
          price_daily: formData.price_daily ? parseFloat(formData.price_daily) : undefined,
          price_weekly: formData.price_weekly ? parseFloat(formData.price_weekly) : undefined,
          price_monthly: formData.price_monthly ? parseFloat(formData.price_monthly) : undefined,
          minRentalDays: formData.minRentalDays ? parseInt(formData.minRentalDays, 10) : undefined,
          weightLbs: formData.weightLbs ? parseFloat(formData.weightLbs) : undefined,
          lengthIn: formData.lengthIn ? parseFloat(formData.lengthIn) : undefined,
          widthIn: formData.widthIn ? parseFloat(formData.widthIn) : undefined,
          heightIn: formData.heightIn ? parseFloat(formData.heightIn) : undefined,
          securityDeposit: requiresDeposit && formData.securityDeposit ? parseFloat(formData.securityDeposit) : undefined,
          listingType: 'RENT',
          photos: uploadedTempImages.map((img, index) => ({
            id: img.id,
            format: img.format,
            originalFilename: img.originalFilename,
            isDefault: index === defaultImageIndex,
          })),
          defaultImageId: previewMeta[defaultImageIndex]?.tempId || uploadedTempImages[defaultImageIndex]?.id
        })
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.details && Array.isArray(data.details) && data.details.length > 0) {
          const messages = data.details.map((d: any) => d.message).join('; ');
          setError(messages);
        } else {
          setError(data.error || 'Failed to create listing');
        }
        setIsLoading(false);
        return;
      }

      setHasListed(true);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Network error. Please try again.');
      setIsLoading(false);
    }
  };

  const styles = {
    '--bg-primary': '#F7FAF7',
    '--forest-green': '#1B5E20',
    '--emerald': '#2E7D32',
    '--bg-tint': '#E8F5E9',
    '--slate': '#4A5568',
    '--slate-light': '#718096',
    '--border': '#E2E8F0',
    '--text-primary': '#1A202C',
    '--text-secondary': '#4A5568',
    '--text-muted': '#A0AEC0',
    '--error-bg': '#FED7D7',
    '--error-text': '#C53030',
    '--error-border': '#FC8181',
  } as React.CSSProperties;

  // An item for rent needs a physical pickup address on the owner's profile (street, city, state, ZIP).
  const hasPickupAddress = hasPhysicalAddress(user?.metadata?.locations?.pickup as any);
  const isSubmitting = isLoading || isUploading;

  useEffect(() => {
    return () => {
      void stopScanner();
      if (hasListed) return;
      const ids = previewMeta
        .map(m => m.tempId)
        .filter((id): id is string => !!id);
      if (ids.length === 0) return;
      const token = localStorage.getItem('token');
      if (!token) return;
      void cleanupTempImages(ids);
    };
  }, []);

  return (
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', ...styles}}>
      {/* Header */}
      <header style={{
        background: '#fff',
        borderBottom: '1px solid var(--border)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '1rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 clamp(1rem, 4vw, 2rem)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link to={user ? '/dashboard' : '/'} style={{display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none'}}>
            <img src="/IGotThat_logo_bk.svg" alt="I Got That U.S." width={230} height={34} style={{width: '230px', height: 'auto', display: 'block'}} />
          </Link>
          <Link to="/dashboard" style={{color: 'var(--slate)', textDecoration: 'none', fontSize: '0.9rem'}}>
            ← Back to Dashboard
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main style={{paddingTop: '0', paddingBottom: '80px', background: 'var(--bg-primary)', minHeight: 'calc(100vh - 72px)'}}>
        <div style={{width: '100%', maxWidth: '640px', padding: '1rem', margin: '0 auto'}}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--border)',
            padding: 'clamp(1.5rem, 5vw, 2.5rem)'
          }}>
            <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
              <h1 style={{fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem 0'}}>
                List Your Item
              </h1>
              <p style={{color: 'var(--slate)', margin: 0}}>
                Share your items with neighbors and earn money
              </p>
            </div>

            {error && (
              <div style={{marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'var(--error-bg)', color: 'var(--error-text)', fontSize: '0.875rem', border: '1px solid var(--error-border)'}}>
                {error}
              </div>
            )}

            {!hasPickupAddress && (
              <div role="alert" style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.875rem 1rem', marginBottom: '1.25rem', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', color: '#92400E', fontSize: '0.875rem'}}>
                <span aria-hidden="true">&#9888;</span>
                <span>
                  You need a pickup address (street, city, state and ZIP) before you can list an item for rent, so renters know where to collect it.{' '}
                  <Link to="/profile?tab=locations" style={{fontWeight: 600, textDecoration: 'underline', color: 'inherit'}}>Add a pickup address</Link>
                </span>
              </div>
            )}
            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '1.25rem'}}>
              {/* Item Name */}
              <div>
                <label htmlFor="title" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Item Name <span style={{color: 'var(--error-text)'}}>*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                  placeholder="e.g., Professional Tile Cutter"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Description <span style={{color: 'var(--error-text)'}}>*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  maxLength={2000}
                  style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', resize: 'vertical'}}
                  placeholder="Describe your item, its condition, and any important details..."
                />
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem'}}>
                  <span style={{fontSize: '0.75rem', color: 'var(--text-muted)'}}>
                    Description must be 20–2000 characters.
                  </span>
                  <span style={{fontSize: '0.75rem', fontWeight: 500, color: (
                    formData.description.length === 0 ? 'var(--text-muted)' :
                    formData.description.length < 20 ? 'var(--error-text)' :
                    formData.description.length <= 2000 ? 'var(--forest-green)' : 'var(--error-text)'
                  )}}>
                    {formData.description.length}/2000
                  </span>
                </div>
              </div>

              {/* Category & Location */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '1rem'}} className="sm:grid-cols-2">
                <div>
                  <label htmlFor="category" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                    Category <span style={{color: 'var(--error-text)'}}>*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white'}}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="condition" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                    Condition <span style={{color: 'var(--error-text)'}}>*</span>
                  </label>
                  <select
                    id="condition"
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white'}}
                    required
                  >
                    {conditions.map(cond => (
                      <option key={cond.value} value={cond.value}>{cond.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pickup Location Selector */}
              <div>
                <label htmlFor="pickupLocationLabel" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Pickup Location <span style={{color: 'var(--error-text)'}}>*</span>
                </label>
                {pickupLocations.length > 0 ? (
                  <select
                    id="pickupLocationLabel"
                    name="pickupLocationLabel"
                    value={formData.pickupLocationLabel}
                    onChange={handleChange}
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', backgroundColor: 'white'}}
                  >
                    {pickupLocations.map(loc => (
                      <option key={loc.id} value={loc.name}>
                        {loc.name}{loc.address ? ` — ${loc.address}` : ''}{loc.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p style={{fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.75rem', background: 'var(--bg-tint)', borderRadius: '8px'}}>
                    No pickup locations saved.{' '}
                    <Link to="/profile" style={{color: 'var(--forest-green)', fontWeight: 500}}>Add one in Profile &gt; Locations</Link>
                    {' '}so renters know where to pick up.
                  </p>
                )}
              </div>

              {/* SKU & Serial Number */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '1rem'}} className="sm:grid-cols-2">
                <div>
                  <label htmlFor="sku" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                    SKU / Model Number
                  </label>
                  <input
                    id="sku"
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="e.g., DW735"
                  />
                </div>
                <div style={{position: 'relative'}}>
                  <label htmlFor="serialNumber" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                    Serial Number <span style={{color: 'var(--error-text)'}}>*</span>
                  </label>
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    <input
                      id="serialNumber"
                      type="text"
                      name="serialNumber"
                      value={formData.serialNumber}
                      onChange={handleChange}
                      style={{flex: 1, padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                      placeholder="Found on the item label"
                    />
                    <button
                      type="button"
                      onClick={showScanner ? stopScanner : startScanner}
                      style={{
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--bg-tint)',
                        color: 'var(--forest-green)',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {showScanner ? 'Stop scan' : 'Scan barcode'}
                    </button>
                  </div>
                  {showScanner && (
                    <div style={{marginTop: '0.75rem', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: '#000'}}>
                      <div id="serial-scanner-region" style={{width: '100%', minHeight: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.875rem'}}>
                        Camera preview will appear here.
                      </div>
                    </div>
                  )}
                  {scannerError && (
                    <div style={{marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--error-text)'}}>
                      {scannerError}
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.5rem'}}>
                  Pricing <span style={{color: 'var(--error-text)'}}>*</span>
                </label>
                <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem'}} className="sm:grid-cols-3">
                  <div>
                    <label htmlFor="price_daily" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>
                      Daily ($)
                    </label>
                    <input
                      id="price_daily"
                      type="text"
                      inputMode="decimal"
                      name="price_daily"
                      value={formData.price_daily}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                      placeholder="25.00"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="price_weekly" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>
                      Weekly ($)
                    </label>
                    <input
                      id="price_weekly"
                      type="text"
                      inputMode="decimal"
                      name="price_weekly"
                      value={formData.price_weekly}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                      placeholder="150.00"
                    />
                  </div>

                  <div>
                    <label htmlFor="price_monthly" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>
                      Monthly ($)
                    </label>
                    <input
                      id="price_monthly"
                      type="text"
                      inputMode="decimal"
                      name="price_monthly"
                      value={formData.price_monthly}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                      placeholder="500.00"
                    />
                  </div>
                </div>
              </div>

              {/* Minimum Rental Duration */}
              <div>
                <label htmlFor="minRentalDays" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Minimum Rental (days)
                </label>
                <input
                  id="minRentalDays"
                  type="number"
                  name="minRentalDays"
                  value={formData.minRentalDays}
                  onChange={handleChange}
                  min="1"
                  max="365"
                  style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                  placeholder="e.g., 2 (leave blank for no minimum)"
                />
                <p style={{fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem'}}>
                  Require renters to keep the item for at least this many days. Leave blank to allow 1-day rentals.
                </p>
              </div>

              {/* Security Deposit */}
              <div>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={requiresDeposit}
                    onChange={(e) => {
                      setRequiresDeposit(e.target.checked);
                      if (!e.target.checked) {
                        setFormData(prev => ({ ...prev, securityDeposit: '' }));
                      }
                    }}
                    style={{width: '1.1rem', height: '1.1rem', accentColor: 'var(--forest-green)'}}
                  />
                  This item requires a deposit
                </label>
                {requiresDeposit && (
                  <div style={{marginTop: '0.5rem'}}>
                    <label htmlFor="securityDeposit" style={{display: 'block', fontSize: '0.75rem', color: 'var(--slate-light)', marginBottom: '0.25rem'}}>
                      Deposit amount ($)
                    </label>
                    <input
                      id="securityDeposit"
                      type="text"
                      inputMode="decimal"
                      name="securityDeposit"
                      value={formData.securityDeposit}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                      placeholder="e.g., 50.00"
                    />
                    <p style={{fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem'}}>
                      Refundable amount collected upfront and shown to renters as a separate line item.
                    </p>
                  </div>
                )}
              </div>

              {/* Weight & Dimensions */}
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Weight & Dimensions (optional)
                </label>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem'}}>
                  <input
                    id="weightLbs"
                    type="number"
                    inputMode="decimal"
                    name="weightLbs"
                    value={formData.weightLbs}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Weight (lbs)"
                  />
                  <input
                    id="lengthIn"
                    type="number"
                    inputMode="decimal"
                    name="lengthIn"
                    value={formData.lengthIn}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Length (in)"
                  />
                  <input
                    id="widthIn"
                    type="number"
                    inputMode="decimal"
                    name="widthIn"
                    value={formData.widthIn}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Width (in)"
                  />
                  <input
                    id="heightIn"
                    type="number"
                    inputMode="decimal"
                    name="heightIn"
                    value={formData.heightIn}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem'}}
                    placeholder="Height (in)"
                  />
                </div>
                <p style={{fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem'}}>
                  Used to estimate shipping cost and check DoorDash eligibility. Leave blank if unknown.
                </p>
              </div>

              {/* Image Upload */}
              <ImageUpload
                onImagesSelected={handleImagesSelected}
                onImageRemove={handleImageRemove}
                onSetDefault={handleSetDefault}
                previewUrls={previewMeta.map(m => m.url)}
                defaultIndex={defaultImageIndex}
                uploadProgress={uploadProgress}
                maxImages={8}
              />

              {/* Buttons */}
              <div style={{display: 'flex', gap: '1rem', marginTop: '0.5rem'}} className="flex-col sm:flex-row">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    background: 'white',
                    color: 'var(--text-secondary)',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !hasPickupAddress}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    background: 'var(--forest-green)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.5 : 1
                  }}
                >
                  {isUploading ? 'Uploading...' : isLoading ? 'Creating...' : 'List Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <Modal
        isOpen={showSerialWarning}
        title="Warning: Serial number missing"
        confirmLabel="Continue anyway"
        cancelLabel="Add serial number"
        onConfirm={() => {
          setShowSerialWarning(false);
          void doSubmit();
        }}
        onCancel={() => {
          setShowSerialWarning(false);
          document.getElementById('serialNumber')?.focus();
        }}
        confirmVariant="danger"
      >
        <div className="space-y-3 text-[var(--text-secondary)]">
          <p>
            You have not entered a serial number. Without it, you may not be able to prove that the item returned is the same one you rented out.
          </p>
          <p className="text-sm">
            Including a serial number protects both you and the renter if there is ever a dispute about the item's identity or condition.
          </p>
        </div>
      </Modal>

      {/* Bottom snackbar for errors */}
      <div
        style={{
          position: 'fixed',
          bottom: isSnackbarVisible ? '1.5rem' : '-6rem',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'min(90vw, 640px)',
          padding: '0.875rem 1.25rem',
          borderRadius: '12px',
          background: 'var(--error-text)',
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          transition: 'bottom 0.3s ease',
          zIndex: 2000,
          pointerEvents: 'none',
          textAlign: 'center'
        }}
      >
        {snackbarMessage}
      </div>
    </div>
  );
}
